// /agents: the agent builder (RFC-060 model 2, byj-1wv.115.12) — the agents I
// made, each with whether it is shared and answers now, what it cost and how
// its last test went, as the main server keeps them while my machine is off;
// a new one, made on my machine; and those given to me to try on my own
// Claude (model 7), made a project of mine there. Agents are made and run on
// my machine, so with none running the page says so and where to start one.
import { useCallback, useEffect, useState } from "react";
import { api, type AppTemplate, type BuiltAgent } from "../api";
import { agentNameRe, agentStanding, agentState, deleteAgent, useBench, useMachine } from "../builder";
import type { SocketLike } from "../live";
import { useConfirm } from "../components/Confirm";
import { FormDialog } from "../components/FormDialog";
import { Link } from "../components/Nav";
import { Page, PageHead, PageLead, PageLoading, PageNotice } from "../components/Page";
import { RowMenu, type MenuItem } from "../components/RowMenu";
import { EmptyRows, Grouped, initialOf } from "../components/Rows";
import { navigate } from "../router";
import { t, whenMinute, whenShort } from "../i18n";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** One of my agents as /shares' rows are: its state, name, how it stands, when, and a ⋯ (byj-1wv.115.49). */
function AgentRow({ a, menu }: { a: BuiltAgent; menu: MenuItem[] }) {
  const to = `/agents/${encodeURIComponent(a.name)}`;
  const answering = agentState(a) === "live";
  return (
    <li className={`share-row${answering ? " is-live" : ""}`} data-testid="agent-row">
      <span className="row-lead">
        <span className="state-dot" aria-hidden="true" />
      </span>
      <span className="kind-box" aria-hidden="true">
        {initialOf(a.name)}
      </span>
      <div className="row-main">
        <Link to={to} className="row-title">
          {a.name}
        </Link>
        <div className="row-sub">
          {agentStanding(a)}
          {a.test && ` · ${t("agents:testSummary", { passed: a.test.passed, of: a.test.of })}`}
          {a.cost_usd > 0 && ` · ${t("agents:cost", { usd: a.cost_usd.toFixed(2) })}`}
        </div>
      </div>
      <div className="row-meta">
        <time className="row-time" dateTime={a.updated_at} title={whenMinute(a.updated_at)}>
          {whenShort(a.updated_at)}
        </time>
      </div>
      <RowMenu label={t("shares:moreFor", { title: a.name })} items={menu} />
    </li>
  );
}

function GivenRow({ g, onTake, can }: { g: AppTemplate; onTake: (g: AppTemplate) => void; can: boolean }) {
  return (
    <li className="share-row no-menu" data-testid="given-row">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        {initialOf(g.agent ?? g.name)}
      </span>
      <div className="row-main">
        <span className="row-title">{g.agent}</span>
        {/* what it is for, read before a copy is made; its instruction and the rest are the copy's */}
        {g.agent_description && (
          <div className="row-sub given-says" data-testid="given-says">
            {g.agent_description}
          </div>
        )}
        <div className="row-sub">{t("agents:givenBy", { owner: g.owner, n: g.latest })}</div>
      </div>
      <div className="row-meta">
        <button className="small" disabled={!can} title={can ? undefined : t("agents:needsMachine")} onClick={() => onTake(g)}>
          {t("agents:takeCopy")}
        </button>
      </div>
    </li>
  );
}

/** `socket` is a test's: the machine's workbench, as the page opens it. */
export function Agents({ socket }: { socket?: (url: string) => SocketLike } = {}) {
  const [data, setData] = useState<{ agents: BuiltAgent[]; given: AppTemplate[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [making, setMaking] = useState(false);
  const [name, setName] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const { machine, error: machineError } = useMachine();
  const bench = useBench(machine?.share_id, socket);
  const [ask, confirmDialog] = useConfirm();
  const load = useCallback(async () => {
    try {
      setData(await api.agents());
      setError(null);
    } catch (err) {
      setError(errText(err));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const take = async (g: AppTemplate) => {
    setProblem(null);
    try {
      await bench.call("builder/take", { template: g.id, name: g.agent });
      await api.putAgent(g.agent!, {});
      navigate(`/agents/${encodeURIComponent(g.agent!)}`);
    } catch (err) {
      setProblem(errText(err));
    }
  };
  if (error) return <PageNotice error={error} />;
  if (!data || machine === undefined) return <PageLoading />;
  const can = !!machine && bench.ready;
  // Open, then Delete with the machine running: deleted with it off, its
  // project would reopen its share when the machine next runs
  const menu = (a: BuiltAgent): MenuItem[] => [
    { label: t("agents:open"), onSelect: () => navigate(`/agents/${encodeURIComponent(a.name)}`) },
    ...(can
      ? [
          {
            label: t("agents:delete"),
            danger: true,
            onSelect: async () => {
              if (!(await ask(t("agents:deleteAsk", { name: a.name }), { action: t("agents:delete"), danger: true }))) return;
              setProblem(null);
              try {
                await deleteAgent(bench.call, a.name, a, () => ask(t("agents:forgetAsk", { name: a.name }), { action: t("agents:forget"), danger: true }));
              } catch (err) {
                setProblem(errText(err));
              }
              // read again either way: a delete done on the machine and not
              // yet forgotten is tried again as not shared. A read that fails
              // here is said above the list, never in the page's place
              try {
                setData(await api.agents());
              } catch (err) {
                setProblem((p) => p ?? errText(err));
              }
            },
          },
        ]
      : []),
  ];
  return (
    <Page>
      <PageHead
        title={t("agents:title")}
        actions={
          <button className="primary" aria-controls="new-agent" disabled={!can} onClick={() => setMaking(true)} data-testid="new-agent">
            {t("agents:newAgent")}
          </button>
        }
      />
      <PageLead>
        <p>{t("agents:lead")}</p>
        {!machine && (
          <p data-testid="no-machine">
            {t("agents:machineOff")} <Link to="/machines">{t("agents:startMachine")}</Link>
          </p>
        )}
        {machine && !bench.ready && <p className="muted small">{t("agents:opening")}</p>}
        {machineError && <p className="error">{t("common:error", { m: machineError })}</p>}
        {problem && <p className="error">{t("common:error", { m: problem })}</p>}
      </PageLead>
      <div className="rows-panel">
        <Grouped
          id="agents"
          headed
          groups={[
            { title: t("agents:mine"), items: data.agents, none: <EmptyRows text={t("agents:none")} />, testid: "agent-list" },
            ...(data.given.length > 0 ? [{ title: t("agents:given"), items: data.given, testid: "given-list", note: <p className="muted small flush" data-testid="given-note">{t("agents:givenNote")}</p> }] : []),
          ]}
          row={(x: BuiltAgent | AppTemplate) =>
            "live" in x ? <AgentRow key={x.name} a={x} menu={menu(x)} /> : <GivenRow key={x.id} g={x} can={can} onTake={(g) => void take(g)} />
          }
        />
      </div>
      {making && (
        <FormDialog
          id="new-agent"
          title={t("agents:newAgent")}
          submitLabel={t("agents:make")}
          canSubmit={agentNameRe.test(name)}
          onClose={() => setMaking(false)}
          onSubmit={async () => {
            await bench.call("builder/new", { name });
            await api.putAgent(name, {});
            navigate(`/agents/${encodeURIComponent(name)}`);
          }}
        >
          <label className="stack">
            <span>{t("agents:name")}</span>
            <input value={name} onChange={(e) => setName(e.target.value.trim().toLowerCase())} placeholder="support-faq" autoFocus />
            <span className="muted small">{t("agents:nameHint")}</span>
          </label>
        </FormDialog>
      )}
      {confirmDialog}
    </Page>
  );
}
